import React, { useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { Filter, FilterActions, FilterAlert, FilterAlertIcon, FilterContent, FilterSelectNative } from '@/components/custom/filter';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { ExamsResolutionCard } from '@/pages/exams/exams-resolution/exams-resolution-card';
import { useQuery } from 'jsonapi-react';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle } from 'react-icons/fi';
import { CardWrapper } from '@/components/ui/card';
import { FilterSelectSearch } from '@/components/custom/filter';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

export function ExamsResolutionList({ courses }) {
  const { t } = useTranslation();
  const playfulStudent = usePlayfulStudent();
  const { data: classrooms = [] } = useQuery('/classrooms?simple=true');
  const { data: exams = [], error: errorExams, isLoading: loading } = useQuery('question_books?filter[visibility]=exam&filter[except_learning_systems]=true');

  useEffect(() => {
    if (errorExams) {
      toast.error(t('toast.errorGetExams'));
    }
  }, [errorExams]);

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      classroom: { value: 'blank', label: t('filter.blankLabelAll') },
      status: {
        approved: false,
        disapproved: false,
        notRealized: false,
        accepting_answers: false,
        review: false
      }
    }
  });

  const filteredClassrooms = useMemo(() => {
    if (filters.values.course === 'blank') return classrooms;
    return classrooms.filter(item => item.course?.id === parseInt(filters.values.course));
  }, [classrooms, filters.values.course]);

  useEffect(() => {
    filters.setFieldValue('classroom', { value: 'blank', label: t('filter.blankLabelAll') });
  }, [filters.values.course]);

  const filteredExams = useMemo(() => {
    if (!exams) return [];

    const selectedStatus = Object.entries(filters.values.status)
      .filter(([_, isChecked]) => isChecked)
      .map(([status]) => (status === 'notRealized' ? null : status));

    const allStatusUnchecked = selectedStatus.length === 0;
    const courseId = filters.values.course;
    const classroomId = filters.values.classroom.value;

    return exams.filter(item => {
      const matchStatus = allStatusUnchecked || selectedStatus.includes(item.status);
      const matchCourse = courseId === 'blank' || item['course-ids']?.includes(Number(courseId));
      const matchClassroom = classroomId === 'blank' || item['classroom-ids']?.includes(Number(classroomId));
      return matchStatus && matchCourse && matchClassroom;
    });
  }, [exams, filters.values.status, filters.values.course, filters.values.classroom]);

  const handleFilterStatus = field => {
    filters.setFieldValue('status', {
      ...filters.values.status,
      [field]: !filters.values.status[field]
    });
  };

  const statusOptions = [
    { label: 'Aprovado', value: filters.values.status.approved, onChange: () => handleFilterStatus('approved') },
    { label: 'Reprovado', value: filters.values.status.disapproved, onChange: () => handleFilterStatus('disapproved') },
    { label: 'Em análise', value: filters.values.status.review, onChange: () => handleFilterStatus('review') },
    { label: 'Aceitando respostas', value: filters.values.status.accepting_answers, onChange: () => handleFilterStatus('accepting_answers') },
    { label: t('filter.unrealized'), value: filters.values.status.notRealized, onChange: () => handleFilterStatus('notRealized') }
  ];

  return (
    <>
      {!playfulStudent && (
        <Filter>
          <FilterContent>
            <FilterSelectNative
              name="course"
              label={t('filter.labelSelectCourse')}
              blankLabel={t('filter.blankLabelAll')}
              value={filters.values.course}
              onChange={filters.handleChange}
              options={courses}
            />

            <FilterSelectSearch
              label="Selecione uma turma"
              value={filters.values.classroom}
              setValue={val => filters.setFieldValue('classroom', val)}
              options={filteredClassrooms?.map(item => ({
                value: item.id,
                label: `${item?.institution?.name || ''} - ${item?.course?.title || ''} - ${item.title}`
              }))}
            />

            <FilterSelectMulti
              label={t('filter.labelSelectStatus')}
              data={statusOptions}
            />
          </FilterContent>

          <FilterActions>
            <FilterAlert>
              <FilterAlertIcon variant="warning">
                <FiAlertCircle />
              </FilterAlertIcon>
              <span>
                Provas de módulos são acessadas através
                <br /> da navegação das aulas nos cursos.
              </span>
            </FilterAlert>
          </FilterActions>
        </Filter>
      )}

      {!loading && filteredExams.length === 0 && <EmptyState type="content" />}

      {loading && <Loader />}

      {playfulStudent ? (
        <div className="grid animate-fade-in gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {!loading &&
            filteredExams.map(exam => (
              <ExamsResolutionCard
                exam={exam}
                key={exam?.id}
              />
            ))}
        </div>
      ) : (
        <CardWrapper className="animate-fade-in">
          {!loading &&
            filteredExams.map(exam => (
              <ExamsResolutionCard
                exam={exam}
                key={exam?.id}
              />
            ))}
        </CardWrapper>
      )}
    </>
  );
}
